import { isUrlInDomainList } from '@/hooks/useRequiredInterceptor';
import { BRXM_ENDPOINT, PORTAL_ROOT, PORTAL_HOMEPAGE_REDIRECTS } from '@/utils/constants';
import { syncGntcConfig } from '@/utils/syncGntcConfig';
import { shouldRedirectToPath } from '@/utils/contactInformationConditionEvaluator';
import { useLog } from '@/features/shared/Hooks';
import axios from 'axios';
import React from 'react';
import { ContactInfo } from './Contact.types';

/**
 * Modifies the URL based on the contact's information and portal homepage redirects
 *
 * @param originalUrl - The original URL to modify
 * @param contactInfo - The contact information object
 * @param Log - The logging function
 * @returns Modified URL or original URL if no modification needed
 */
const modifyUrlBasedOnContactInfo = (
  originalUrl: string,
  contactInfo: Record<string, any>,
  Log: (...args: any[]) => void,
): string => {
  // Only process resourceapi URLs
  if (!originalUrl.includes(BRXM_ENDPOINT)) {
    return originalUrl;
  }

  // Extract the path after resourceapi
  const resourceApiIndex = originalUrl.indexOf(BRXM_ENDPOINT);
  if (resourceApiIndex === -1) {
    return originalUrl;
  }

  const pathAfterResourceApi = originalUrl.substring(resourceApiIndex + BRXM_ENDPOINT.length);
  Log('📍 Path after resourceapi:', pathAfterResourceApi);

  // Check each redirect rule in the portal homepage redirects
  for (const [redirectToPath, condition] of Object.entries(PORTAL_HOMEPAGE_REDIRECTS)) {
    Log('🔍 Checking redirect rule:', {
      to: redirectToPath,
      condition,
    });

    // Check if the current path matches PORTAL_ROOT (the "from" path is always /portal)
    if (pathAfterResourceApi === PORTAL_ROOT) {
      Log(`✅ Path matches ${PORTAL_ROOT}, checking condition`);

      // Check if the contact meets the redirect condition
      if (
        shouldRedirectToPath(pathAfterResourceApi, condition as string, contactInfo, PORTAL_ROOT)
      ) {
        Log('🎯 Condition met! Redirecting to:', redirectToPath);
        // Redirect to the specified path by modifying the URL
        const newUrl = originalUrl.replace(pathAfterResourceApi, redirectToPath);
        Log('🔄 URL modified for redirect:', {
          original: originalUrl,
          new: newUrl,
        });
        return newUrl;
      } else {
        Log('❌ Condition not met for this rule');
      }
    }
  }

  Log('✅ No redirect needed, keeping original URL');
  return originalUrl;
};

/**
 * Hook that intercepts axios requests to resourceapi and modifies URLs based on contact's information
 * for portal homepage redirection using value list configuration
 *
 * @param contactInfo - Current contact information for the user
 * @param trustedDomains - List of domains that should be intercepted
 * @returns void
 */
export const usePortalHomepageRedirectionInterceptor = ({
  contactInfo,
  trustedDomains,
}: {
  contactInfo: ContactInfo | null;
  trustedDomains: string[];
}): void => {
  // Store contact info in ref to avoid stale closures in interceptor
  const contactInfoRef = React.useRef<ContactInfo | null>(null);
  const Log = useLog({ context: 'usePortalHomepageRedirectionInterceptor' });

  // Keep ref in sync with latest contact info
  React.useEffect(() => {
    contactInfoRef.current = contactInfo;
  }, [contactInfo]);

  // Setup request interceptor
  React.useEffect(() => {
    // Add interceptor to modify URLs based on contact information
    const ejectId = axios.interceptors.request.use(async (config) => {
      Log('🔍 Interceptor triggered for URL:', config.url);

      // Skip interceptor for non-trusted domains
      if (!isUrlInDomainList(config.url, trustedDomains)) {
        Log('❌ URL not in trusted domains, skipping');
        return config;
      }

      // Skip if no contact info
      if (!contactInfoRef.current) {
        Log('❌ No contact info available, skipping');
        return config;
      }

      const currentContactInfo = contactInfoRef.current;

      // Only process requests to PORTAL_ROOT path (exact match)
      if (!config.url || !config.url.endsWith(PORTAL_ROOT)) {
        Log(`❌ URL does not end with ${PORTAL_ROOT}, skipping`);
        return config;
      }

      Log('✅ Processing URL for portal homepage redirection logic');

      // Modify URL based on contact information and portal homepage redirects
      const modifiedUrl = modifyUrlBasedOnContactInfo(config.url, currentContactInfo, Log);

      if (modifiedUrl !== config.url) {
        Log('🔄 URL modified:', {
          original: config.url,
          modified: modifiedUrl,
        });
        return {
          ...config,
          url: modifiedUrl,
        };
      }

      Log('✅ No redirection needed, keeping original URL');
      return config;
    });

    // Sync config on mount
    syncGntcConfig();

    // Cleanup interceptor and sync config on unmount
    return () => {
      axios.interceptors.request.eject(ejectId);
      syncGntcConfig();
    };
  }, [trustedDomains, Log]);
};
